import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

const CacheContext = createContext();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

export function useCache() {
  const ctx = useContext(CacheContext);
  if (!ctx) throw new Error('useCache must be used within CacheProvider');
  return ctx;
}

export function CacheProvider({ children }) {
  const [cache, setCache] = useState({});

  const getCached = useCallback((key) => {
    const cached = cache[key];
    if (!cached) return null;
    if (Date.now() - cached.timestamp > CACHE_DURATION) return null;
    return cached.data;
  }, [cache]);

  const setCached = useCallback((key, data) => {
    setCache((prev) => ({
      ...prev,
      [key]: { data, timestamp: Date.now() },
    }));
  }, []);

  const invalidateCache = useCallback((key) => {
    if (key) {
      setCache((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    } else {
      setCache({});
    }
  }, []);

  const isCacheValid = useCallback((key) => {
    const cached = cache[key];
    if (!cached) return false;
    return Date.now() - cached.timestamp <= CACHE_DURATION;
  }, [cache]);

  const value = { getCached, setCached, invalidateCache, isCacheValid };

  return (
    <CacheContext.Provider value={value}>
      {children}
    </CacheContext.Provider>
  );
}

/**
 * Hook: fetch with cache. Pass cacheKey, fetchFn, and optional dependencies.
 */
export function useCachedFetch(cacheKey, fetchFn, dependencies = []) {
  const { getCached, setCached, isCacheValid } = useCache();
  const [data, setData] = useState(() => getCached(cacheKey));
  const [loading, setLoading] = useState(!getCached(cacheKey));
  const [error, setError] = useState(null);

  const refresh = useCallback(
    async (forceRefresh = false) => {
      if (!forceRefresh && isCacheValid(cacheKey)) {
        const cached = getCached(cacheKey);
        if (cached) {
          setData(cached);
          setLoading(false);
          return cached;
        }
      }
      setLoading(true);
      setError(null);
      try {
        const result = await fetchFn();
        setData(result);
        setCached(cacheKey, result);
        return result;
      } catch (err) {
        setError(err);
        console.error(`Error fetching ${cacheKey}:`, err);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [cacheKey, fetchFn, getCached, setCached, isCacheValid]
  );

  useEffect(() => {
    refresh();
  }, [cacheKey, refresh]);

  return { data, loading, error, refresh };
}
